import clsx from "clsx";
import { Button } from "primereact/button";
import { Calendar, CalendarProps } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { Tooltip } from "primereact/tooltip";
import { FC } from "react";
import {
  Control,
  Controller,
  UseFormRegister,
  UseFormSetValue,
} from "react-hook-form";
import { useToast } from "../../../context/toast/ui";
import { customBase64Uploader } from "../../../utils/file";
import styles from "./field.module.css";
import { TProp } from "./types";

type TFieldComponent = {
  prop: TProp;
  uid: any;
  labelWidth?: number;
  valueWidth?: number;
  reg: UseFormRegister<Record<string, string | number | boolean>>;
  control: Control;
  change?: (field: string, data?: any) => void;
  changeAttribute?: (field: string, attributes: any) => void;
  set?: UseFormSetValue<any>;
};

export const Field: FC<TFieldComponent> = (props) => {
  return (
    <div>
      {/*
                TODO: по дизайну пока такое решение
                Непонятно было как отображать field с загрузкой файла
                Типа вывожу его пока без label
                Но этот момент надо обсуждать
                А все остальные в input-group с label
            */}
      {props.prop.type === "file" && <FieldElement {...props} />}
      {props.prop.type !== "file" && (
        <div className={"p-inputgroup flex-1"}>
          <Tooltip
            target={`.jsTooltip${props.uid}`}
            className={styles.tooltipShow}
          />

          <span
            className={clsx(
              "p-inputgroup-addon",
              styles.resurs__props__field_label,
              `jsTooltip${props.uid}`,
              props.prop.templateOptions.disabled
                ? styles.span__disabled
                : undefined
            )}
            style={{
              width: props.labelWidth,
              cursor: "default",
              height: props.prop.type === "textarea" ? "auto" : "",
              maxHeight: props.prop.type === "textarea" ? "100%" : "",
            }}
            data-pr-tooltip={props.prop.templateOptions.label}
            data-pr-position="left"
          >
            {props.prop.templateOptions.label}
          </span>

          <FieldElement {...props} />
        </div>
      )}
    </div>
  );
};

// Здесь просто поля по типам
const FieldElement: FC<TFieldComponent> = ({
  prop,
  control,
  change,
  changeAttribute,
  valueWidth,
}) => {
  const options = prop.templateOptions.options?.map((opt) => {
    return {
      name: opt.name,
      value: opt.value,
    };
  });

  const { showMessage } = useToast();

  type TMultyOptions = {
    name: string;
    code: boolean;
  };

  return (
    <>
      {prop.type === "file" && (
        <Controller
          control={control}
          name={prop.key}
          render={({ field }) => (
            <FileUpload
              auto
              customUpload
              // TODO: Список разрешенных файлов для разгрузки тоже должно приходить в настройках поля
              accept=".xlsx,.xls"
              // TODO: Максимальный размер - наверно тоже
              maxFileSize={1000000}
              chooseLabel="Выбрать"
              emptyTemplate={
                <p className="m-0">Перетащите сюда файлы для загрузки.</p>
              }
              onRemove={() => {
                field.onChange("");
              }}
              uploadHandler={(event) => {
                customBase64Uploader(event).then((data) => {
                  field.onChange(data);
                  if (changeAttribute) {
                    changeAttribute(prop.key, {
                      type: "file",
                      archiver: "none",
                    });
                  }
                });
              }}
            />
          )}
        />
      )}
      {prop.type === "input" && (
        <Controller
          name={prop.key}
          control={control}
          defaultValue={prop.defaultValue}
          render={({ field, fieldState: { error } }) => (
            <InputText
              id={field.name}
              className={styles.inputText}
              style={{ width: valueWidth }}
              value={field.value}
              type={prop.templateOptions.type}
              maxLength={prop.templateOptions.valid?.max}
              minLength={prop.templateOptions.valid?.min}
              disabled={prop.templateOptions.disabled}
              onChange={(e) => {
                field.onChange(e.target.value);
              }}
              onBlur={(e) => {
                if (change) {
                  change(prop.key, e.target.value);
                }

                const regex = new RegExp(
                  prop.templateOptions.valid?.regex || ""
                );
                if (!regex.test(e.target.value)) {
                  showMessage({
                    caption: "Ошибка",
                    message: prop.templateOptions.valid?.message || "",
                    tag: "props",
                    timeOut: 3,
                    type: "error",
                  });
                }
              }}
            />
          )}
        />
      )}

      {prop.type === "select" && (
        <Controller
          control={control}
          name={prop.key}
          render={({ field }) => (
            <Dropdown
              className={clsx(styles.resurs__props__field, styles.dropdown)}
              disabled={prop.templateOptions.disabled}
              id={field.name}
              value={field.value}
              onChange={(e) => {
                if (change) {
                  change(prop.key, e.value);
                  field.onChange(e);
                }
              }}
              options={options}
              optionLabel="name"
            />
          )}
        />
      )}

      {prop.type === "multi-select" && (
        <Controller
          control={control}
          name={prop.key}
          render={({ field }) => {
            return (
              <>
                {Array.isArray(field.value) && (
                  <MultiSelect
                    value={field.value}
                    useOptionAsValue={true}
                    className={styles.multyselect}
                    display="comma"
                    disabled={prop.templateOptions.disabled}
                    optionLabel={"name"}
                    options={prop.templateOptions.options}
                    onChange={(e) => {
                      field.onChange(e.value);

                      if (change) {
                        change(
                          prop.key,
                          e.value
                            .filter((item: TMultyOptions) => {
                              return item && item.name;
                            })
                            .map((item: TMultyOptions) => item.name)
                            .join(",")
                        );
                      }
                    }}
                    placeholder="Операторы"
                  />
                )}
              </>
            );
          }}
        />
      )}

      {prop.type === "textarea" && (
        <Controller
          control={control}
          name={prop.key}
          render={({ field }) => (
            <InputTextarea
              style={{
                height: "auto",
                maxHeight: "100%",
                borderTopRightRadius: 6,
                borderBottomRightRadius: 6,
              }}
              id={field.name}
              disabled={prop.templateOptions.disabled}
              {...field}
              rows={4}
              cols={30}
              autoResize
              onBlur={(e) => {
                if (change) {
                  change(prop.key, e.target.value);
                }
              }}
            />
          )}
        />
      )}

      {prop.type === "button" && (
        <Controller
          control={control}
          name={"name"}
          render={({ field }) => (
            <Button
              id={field.name}
              label="Проверить"
              disabled={prop.templateOptions.disabled}
              style={{ width: "50%" }}
              onClick={(event) => {
                event.preventDefault();
                if (change) {
                  change(prop.key, "Проверить");
                }
              }}
            />
          )}
        />
      )}

      {prop.type === "date" && (
        <Controller
          name={prop.key}
          disabled={prop.templateOptions.disabled}
          control={control}
          defaultValue={
            prop.defaultValue &&
            !isNaN(+prop.defaultValue) &&
            +prop.defaultValue > 0
              ? +prop.defaultValue
              : undefined
          }
          render={({ field }) => {
            const numericValue =
              field.value !== undefined &&
              field.value !== null &&
              field.value !== "" &&
              !isNaN(Number(field.value)) &&
              Number(field.value) > 0
                ? Number(field.value)
                : null;

            return (
              <Calendar
                disabled={prop.templateOptions.disabled}
                inputClassName={styles.inputText}
                value={numericValue ? new Date(numericValue * 1000) : null}
                onChange={(e) => {
                  const selectedDate = e.value;
                  if (!selectedDate) {
                    field.onChange(null);
                    if (change) {
                      change(prop.key, null);
                    }
                    return;
                  }

                  field.onChange(selectedDate.getTime() / 1000);
                  if (change) {
                    change(prop.key, selectedDate.getTime() / 1000);
                  }
                }}
                dateFormat="dd/mm/yy"
              />
            );
          }}
        />
      )}
    </>
  );
};
